import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import PropTypes from 'prop-types';
import {
  Card,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Typography,
  Chip,
} from '@mui/material';
import _ from 'lodash';
import { ViewTitle } from '../../components/ViewTitle';
import * as actions from '../../redux/actions';
import { BackButton } from '../../components/Button';
import dayjs from 'dayjs';
import { g } from 'addthaifont-pdfmake/build/pdfmake';

export default function EditOrderGold({ title, subtitle }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const history = useHistory();
  const goldReport = useSelector((state) => state.gold);
  const historyTransaction = useSelector((state) => state.gold.history?.data);
  const me = useSelector((state) => state?.me?.userData);
  const transactionType = goldReport?.gold_transaction_type;

  const [customerData, setCustomerData] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(actions.goldTransactionGet(id));
    dispatch(actions.goldTransactionGetHistory(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (goldReport) {
      setCustomerData(goldReport.customer || {});
      setOrderData(goldReport.order || []);
    }
  }, [goldReport]);

  const handleCustomerChange = (field, value) => {
    setCustomerData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSocialChange = (field, value) => {
    setCustomerData((prev) => ({
      ...prev,
      social: {
        ...prev.social,
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleOrderChange = (index, field, value) => {
    setOrderData((prev) => {
      const newData = [...prev];
      newData[index] = {
        ...newData[index],
        [field]: parseFloat(value) || 0,
      };

      const order = newData[index];
      if (order?.isGold_bar === true) {
        order.pricebackperbath = 0;
        order.pricebackpergram = 0;
        order.pricebackpergramfix = 0;
        order.pricebackperbathtotal = 0;
        order.totalbeforeVat = order.cost;
        order.vat = 0;
        order.totalWithVat = order.cost;
      } else if (order?.place === 'คลังอื่นๆ') {
        const vat = (order.cost * 0.07).toFixed(2);
        const totalbeforeVat = (order.cost - vat).toFixed(2);
        order.pricebackperbath = 0;
        order.pricebackpergram = 0;
        order.pricebackpergramfix = 0;
        order.pricebackperbathtotal = 0;
        order.totalbeforeVat = parseFloat(totalbeforeVat);
        order.vat = parseFloat(vat);
        order.totalWithVat = order.cost;
      } else if (
        field === 'weight' ||
        field === 'cost' ||
        field === 'gold_official'
      ) {
        const materialTypeVat = order?.material?.material_type?.vat || 0;
        const pricebackperbath = (order?.gold_official - 100) * 0.98;

        const pricebackpergram =
          materialTypeVat === 1.036
            ? pricebackperbath / 15.16
            : Math.round(pricebackperbath / 15.16);

        let pricebackpergramfix;

        if (materialTypeVat === 1) {
          pricebackpergramfix = pricebackpergram;
        } else if (materialTypeVat === 1.036) {
          pricebackpergramfix =
            Math.round(pricebackpergram * materialTypeVat * 100) / 100;
        } else if (materialTypeVat === 0.35 || materialTypeVat === 0.45) {
          pricebackpergramfix = Math.round(pricebackpergram * materialTypeVat);
        } else {
          pricebackpergramfix =
            Math.round(pricebackpergram * materialTypeVat * 10) / 10;
        }
        const pricebackperbathtotal = (pricebackpergramfix * 15.16).toFixed(2);
        let totalbeforeVat = (
          order?.cost -
          ((order?.cost - pricebackpergramfix * order?.weight) * 7) / 107
        ).toFixed(2);
        const baseVat = (order?.weight * pricebackpergramfix)?.toFixed(2);
        const baseTotalVat =
          totalbeforeVat - baseVat > 0
            ? (totalbeforeVat - baseVat).toFixed(2)
            : 0;

        let vat =
          baseTotalVat > 0 ? (order?.cost - totalbeforeVat).toFixed(2) : 0;
        if (transactionType === 'BUY' && order?.place === 'คลังรูปพรรณเก่า') {
          vat = 0;
          totalbeforeVat = order?.cost;
        } else {
          totalbeforeVat =
            order?.cost - totalbeforeVat > 0 ? totalbeforeVat : order?.cost;
        }

        order.pricebackperbath = pricebackperbath;
        order.pricebackpergram = pricebackpergramfix;
        order.pricebackperbathtotal = Number(pricebackperbathtotal);
        order.totalbeforeVat = Number(totalbeforeVat);
        order.vat = Number(vat);
      }
      // No change to other fields for non-standard conditions
      return newData;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const updateData = {
      customer: {
        _id: customerData._id,
        name: customerData.name,
        born: customerData.born,
        address: customerData.address,
        cardId: customerData.cardId,
        tel: customerData.tel,
        job: customerData.job,
        social: {
          facebook: customerData.social?.facebook || '',
          line: customerData.social?.line || '',
          email: customerData.social?.email || '',
        },
      },
      order: orderData.map((order) => ({
        ...order,
        weight: parseFloat(order.weight) || 0,
        cost: parseFloat(order.cost) || 0,
        gold_official: parseFloat(order.gold_official) || 0,
        price_gold: parseFloat(order.price_gold) || 0,
        pricebackperbath: order.pricebackperbath || 0,
        pricebackpergram: order.pricebackpergram || 0,
        pricebackperbathtotal: order.pricebackperbathtotal || 0,
        totalbeforeVat: order.totalbeforeVat || 0,
        vat: order.vat || 0,
      })),

      editedBy:
        `${me?.firstname || ''} ${me?.lastname || ''}`.trim() || 'Unknown',
      editReason: 'Manual update via web interface',
    };

    try {
      await dispatch(actions.goldTransactionPut(id, updateData));
      setHasChanges(false);
      await dispatch(actions.goldTransactionGet(id));
      await dispatch(actions.goldTransactionGetHistory(id));
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  // Helper function to get change description
  const getChangeDescription = (history) => {
    if (!history.old_data || !history.new_data)
      return ['ไม่มีรายละเอียดการเปลี่ยนแปลง'];

    const descriptions = [];
    const oldData = history.old_data;
    const newData = history.new_data;

    // เปรียบเทียบข้อมูลลูกค้า
    if (oldData.customer && newData.customer) {
      const oldCustomer = oldData.customer;
      const newCustomer = newData.customer;

      if (oldCustomer.name !== newCustomer.name) {
        descriptions.push(
          `เปลี่ยนชื่อลูกค้า: "${oldCustomer.name}" → "${newCustomer.name}"`,
        );
      }
      if (oldCustomer.address !== newCustomer.address) {
        descriptions.push(
          `เปลี่ยนที่อยู่: "${oldCustomer.address}" → "${newCustomer.address}"`,
        );
      }
      if (oldCustomer.cardId !== newCustomer.cardId) {
        descriptions.push(
          `เปลี่ยนบัตรประชาชน: "${oldCustomer.cardId}" → "${newCustomer.cardId}"`,
        );
      }
      if (oldCustomer.job !== newCustomer.job) {
        descriptions.push(
          `เปลี่ยนอาชีพ: "${oldCustomer.job}" → "${newCustomer.job}"`,
        );
      }
      if (oldCustomer.tel !== newCustomer.tel) {
        descriptions.push(
          `เปลี่ยนเบอร์โทร: "${oldCustomer.tel || 'ไม่ระบุ'}" → "${
            newCustomer.tel || 'ไม่ระบุ'
          }"`,
        );
      }
      if (oldCustomer.social?.facebook !== newCustomer.social?.facebook) {
        descriptions.push(
          `เปลี่ยน Facebook: "${
            oldCustomer.social?.facebook || 'ไม่ระบุ'
          }" → "${newCustomer.social?.facebook || 'ไม่ระบุ'}"`,
        );
      }
      if (oldCustomer.social?.line !== newCustomer.social?.line) {
        descriptions.push(
          `เปลี่ยน Line: "${oldCustomer.social?.line || 'ไม่ระบุ'}" → "${
            newCustomer.social?.line || 'ไม่ระบุ'
          }"`,
        );
      }
      if (oldCustomer.social?.email !== newCustomer.social?.email) {
        descriptions.push(
          `เปลี่ยน Email: "${oldCustomer.social?.email || 'ไม่ระบุ'}" → "${
            newCustomer.social?.email || 'ไม่ระบุ'
          }"`,
        );
      }
    }

    // เปรียบเทียบข้อมูลการชำระเงิน
    if (oldData.payment && newData.payment) {
      const oldPayment = oldData.payment;
      const newPayment = newData.payment;

      if (oldPayment.cash?.amount !== newPayment.cash?.amount) {
        descriptions.push(
          `เปลี่ยนจำนวนเงินสด: ${
            oldPayment.cash?.amount?.toLocaleString() || 0
          } → ${newPayment.cash?.amount?.toLocaleString() || 0} บาท`,
        );
      }
      if (oldPayment.transfer?.amount !== newPayment.transfer?.amount) {
        descriptions.push(
          `เปลี่ยนจำนวนเงินโอน: ${
            oldPayment.transfer?.amount?.toLocaleString() || 0
          } → ${newPayment.transfer?.amount?.toLocaleString() || 0} บาท`,
        );
      }
      if (oldPayment.credit?.amount !== newPayment.credit?.amount) {
        descriptions.push(
          `เปลี่ยนจำนวนเงินเครดิต: ${
            oldPayment.credit?.amount?.toLocaleString() || 0
          } → ${newPayment.credit?.amount?.toLocaleString() || 0} บาท`,
        );
      }
    }

    // เปรียบเทียบข้อมูลออเดอร์
    if (oldData.order && newData.order) {
      oldData.order.forEach((oldOrder, index) => {
        const newOrder = newData.order[index];
        if (newOrder) {
          if (oldOrder.weight !== newOrder.weight) {
            descriptions.push(
              `เปลี่ยนน้ำหนักสินค้า ${index + 1}: ${oldOrder.weight} → ${
                newOrder.weight
              } กรัม`,
            );
          }
          if (oldOrder.cost !== newOrder.cost) {
            descriptions.push(
              `เปลี่ยนราคาสินค้า ${
                index + 1
              }: ${oldOrder.cost?.toLocaleString()} → ${newOrder.cost?.toLocaleString()} บาท`,
            );
          }
          if (oldOrder.price_gold !== newOrder.price_gold) {
            descriptions.push(
              `เปลี่ยนราคาทอง ${
                index + 1
              }: ${oldOrder.price_gold?.toLocaleString()} → ${newOrder.price_gold?.toLocaleString()} บาท`,
            );
          }
        }
      });
    }

    return descriptions.length > 0 ? descriptions : ['มีการอัพเดทข้อมูล'];
  };

  const renderTitle = () => <ViewTitle title={title} subtitle={subtitle} />;

  const renderCustomerForm = () => (
    <Card className="p-4 mb-4">
      <Typography variant="h6" className="mb-4 font-bold text-gray-700">
        ข้อมูลลูกค้า
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="ชื่อลูกค้า"
            value={customerData.name || ''}
            onChange={(e) => handleCustomerChange('name', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="วันเกิด"
            type="date"
            value={
              customerData.born
                ? dayjs(customerData.born).format('YYYY-MM-DD')
                : ''
            }
            onChange={(e) => handleCustomerChange('born', e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="ที่อยู่"
            multiline
            rows={2}
            value={customerData.address || ''}
            onChange={(e) => handleCustomerChange('address', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="เลขบัตรประชาชน"
            value={customerData.cardId || ''}
            onChange={(e) => handleCustomerChange('cardId', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="หมายเลขโทรศัพท์"
            value={customerData.tel || ''}
            onChange={(e) => handleCustomerChange('tel', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="อาชีพ"
            value={customerData.job || ''}
            onChange={(e) => handleCustomerChange('job', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Facebook"
            value={customerData.social?.facebook || ''}
            onChange={(e) => handleSocialChange('facebook', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Line ID"
            value={customerData.social?.line || ''}
            onChange={(e) => handleSocialChange('line', e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            value={customerData.social?.email || ''}
            onChange={(e) => handleSocialChange('email', e.target.value)}
            size="small"
          />
        </Grid>
      </Grid>
    </Card>
  );

  const renderOrderForm = () => (
    <Card className="p-4 mb-4">
      <Typography variant="h6" className="mb-4 font-bold text-gray-700">
        รายการสินค้า
      </Typography>
      <div className="overflow-x-auto">
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead className="bg-yellow-100">
              <TableRow>
                <TableCell align="center" className="font-bold">
                  ลำดับ
                </TableCell>
                <TableCell align="center" className="font-bold">
                  สินค้า
                </TableCell>
                <TableCell align="center" className="font-bold">
                  ประเภท
                </TableCell>
                <TableCell align="center" className="font-bold">
                  น้ำหนัก (กรัม)
                </TableCell>
                <TableCell align="center" className="font-bold">
                  จำนวนเงิน (บาท)
                </TableCell>
                <TableCell align="center" className="font-bold">
                  ราคาทองสมาคม (บาท)
                </TableCell>
                <TableCell align="center" className="font-bold">
                  ราคาสินค้าก่อนภาษีมูลค่าเพิ่ม (บาท)
                </TableCell>
                <TableCell align="center" className="font-bold">
                  ราคาซื้อคืน/กรัม
                </TableCell>
                <TableCell align="center" className="font-bold">
                  ราคาซื้อคืน/บาท
                </TableCell>
                <TableCell align="center" className="font-bold">
                  ภาษี
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderData.map((order, index) => (
                <TableRow
                  key={index}
                  className={index % 2 === 0 ? 'bg-gray-50' : ''}
                >
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    {order.material?.name || '-'}
                  </TableCell>
                  <TableCell align="center">
                    {order.material?.material_type?.name || '-'}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={order.weight || ''}
                      onChange={(e) =>
                        handleOrderChange(index, 'weight', e.target.value)
                      }
                      size="small"
                      className="w-20"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={order.cost || ''}
                      onChange={(e) =>
                        handleOrderChange(index, 'cost', e.target.value)
                      }
                      size="small"
                      className="w-24"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={order.gold_official || ''}
                      onChange={(e) =>
                        handleOrderChange(
                          index,
                          'gold_official',
                          e.target.value,
                        )
                      }
                      size="small"
                      className="w-24"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {order.totalbeforeVat?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell align="center">
                    {order.pricebackpergram?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell align="center">
                    {order.pricebackperbath?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell align="center">
                    {order.vat?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Card>
  );

  const renderEditHistory = () => (
    <Card className="p-4 mb-4">
      <Typography variant="h6" className="mb-4 font-bold text-gray-700">
        ประวัติการแก้ไข
      </Typography>
      {!historyTransaction || historyTransaction.length === 0 ? (
        <div className="text-center py-8">
          <Typography variant="body1" className="text-gray-500">
            ไม่มีประวัติการแก้ไข
          </Typography>
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead className="bg-gray-100">
              <TableRow>
                <TableCell align="center" className="font-bold">
                  วันที่แก้ไข
                </TableCell>
                <TableCell align="center" className="font-bold">
                  ผู้แก้ไข
                </TableCell>
                <TableCell align="left" className="font-bold">
                  รายละเอียดการเปลี่ยนแปลง
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyTransaction.map((history, index) => (
                <TableRow
                  key={history._id || index}
                  className={index % 2 === 0 ? 'bg-gray-50' : ''}
                >
                  <TableCell align="center">
                    {dayjs(history.modified_at || history.createdAt).format(
                      'DD/MM/YYYY HH:mm',
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {history.modified_by || 'ไม่ระบุผู้แก้ไข'}
                  </TableCell>
                  <TableCell align="left">
                    <div className="text-sm">
                      {getChangeDescription(history).map((change, i) => (
                        <div key={i} className="text-gray-600 mb-1">
                          • {change}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );

  return (
    <div className="p-4">
      <div>{renderTitle()}</div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="order-2 sm:order-1">
          <BackButton />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2 w-full sm:w-auto">
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors w-full sm:w-auto"
          >
            ยกเลิก
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`px-4 py-2 rounded transition-colors w-full sm:w-auto ${
              hasChanges && !saving
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {renderCustomerForm()}
        {renderOrderForm()}
        {renderEditHistory()}
      </div>
    </div>
  );
}

EditOrderGold.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

EditOrderGold.defaultProps = {
  title: 'แก้ไขรายการสั่งซื้อทอง',
  subtitle: '',
};
